/**
 * Serialization and data format utilities for mind map data
 */

import { MindMapState } from '../../models/MindMapState';
//import { Parent } from '../../models/Parent';
//import { RelativePosition } from '../../models/RelativePosition';

export const FORMATS = {
  CURRENT: '2.0',
  LEGACY_1_0: '1.0'
};

export const EXPORT_FORMATS = {
  JSON: 'json',
  XML: 'xml',
  CSV: 'csv',
  MARKDOWN: 'markdown'
};

const generateImportedNodeId = () => crypto.randomUUID();

export class MindMapSerializer {
  static toJSON(mindMapState, options = {}) {
    const { includeMetadata = true, pretty = false } = options;
    const data = {
      version: FORMATS.CURRENT,
      ...mindMapState.toJSON()
    };
    if (includeMetadata) {
      data.metadata = {
        exportedAt: new Date().toISOString(),
        statistics: mindMapState.getStatistics()
      };
    }
    return pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
  }

  static fromJSON(jsonString) {
    const data = JSON.parse(jsonString);
    if (data.version === FORMATS.LEGACY_1_0) {
      return this.migrateLegacyFormat(data);
    }
    return new MindMapState(data);
  }

  static migrateLegacyFormat(legacyData) {
    console.warn('Migrating from legacy format v1.0');
    return new MindMapState({
      nodes: legacyData.nodes || [],
      relationships: legacyData.relationships || [],
      root: legacyData.root || null,
      selectedId: legacyData.selectedId || null
    });
  }

  static export(mindMapState, format, options = {}) {
    switch (format) {
      case EXPORT_FORMATS.JSON: return this.toJSON(mindMapState, options);
      case EXPORT_FORMATS.XML: return this.toXML(mindMapState, options);
      case EXPORT_FORMATS.CSV: return this.toCSV(mindMapState, options);
      case EXPORT_FORMATS.MARKDOWN: return this.toMarkdown(mindMapState, options);
      default: throw new Error(`Unsupported export format: ${format}`);
    }
  }

  static toXML(mindMapState, options = {}) {
    const { includePositions = true } = options;
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<mindmap>\n';
    xml += `  <metadata version="${FORMATS.CURRENT}" exportedAt="${new Date().toISOString()}"/>\n  <nodes>\n`;

    mindMapState.nodes.forEach(node => {
      xml += `    <node id="${this.escapeXml(node.id)}" isRoot="${node.isRoot}">\n`;
      xml += `      <text><![CDATA[${node.text}]]></text>\n`;
      xml += `      <color>${this.escapeXml(node.color)}</color>\n`;
      if (includePositions) {
        xml += `      <position x="${node.position.x}" y="${node.position.y}"/>\n`;
      }
      xml += '    </node>\n';
    });

    xml += '  </nodes>\n  <relationships>\n';
    mindMapState.relationships.forEach(rel => {
      xml += `    <relationship parentId="${this.escapeXml(rel.parentId)}" childId="${this.escapeXml(rel.childId)}"/>\n`;
    });
    xml += '  </relationships>\n</mindmap>';
    return xml;
  }

  static toCSV(mindMapState, options = {}) {
    const { includePositions = false } = options;
    let csv = 'id,text,parentId,depth,isRoot';
    if (includePositions) csv += ',x,y';
    csv += ',color\n';

    mindMapState.nodes.forEach(node => {
      const parentId = mindMapState.getParent(node.id) || '';
      const depth = mindMapState.getNodeDepth(node.id);
      const text = this.escapeCsv(node.text);
      csv += `${node.id},${text},${parentId},${depth},${node.isRoot}`;
      if (includePositions) csv += `,${node.position.x},${node.position.y}`;
      csv += `,${this.escapeCsv(node.color)}\n`;
    });
    return csv;
  }

  static toMarkdown(mindMapState, options = {}) {
    const { useNumbering = false } = options;
    if (!mindMapState.root) return '# Empty Mind Map\n';
    const rootNode = mindMapState.nodes.find(n => n.id === mindMapState.root);
    if (!rootNode) return '# Empty Mind Map\n';
    return `# ${rootNode.text}\n\n${this.nodeToMarkdown(mindMapState, rootNode.id, 0, useNumbering)}`;
  }

  static nodeToMarkdown(mindMapState, nodeId, depth, useNumbering) {
    let markdown = '';
    const children = mindMapState.getChildren(nodeId);
    children.forEach((childId, index) => {
      const childNode = mindMapState.nodes.find(n => n.id === childId);
      if (!childNode) return;
      const indent = '  '.repeat(depth);
      const prefix = useNumbering ? `${index + 1}. ` : '- ';
      markdown += `${indent}${prefix}${childNode.text}\n`;
      markdown += this.nodeToMarkdown(mindMapState, childId, depth + 1, useNumbering);
    });
    return markdown;
  }

  static import(data, format, options = {}) {
    switch (format) {
      case EXPORT_FORMATS.JSON: return this.fromJSON(data);
      case EXPORT_FORMATS.XML: return this.fromXML(data, options);
      case EXPORT_FORMATS.CSV: return this.fromCSV(data, options);
      case EXPORT_FORMATS.MARKDOWN: return this.fromMarkdown(data, options);
      default: throw new Error(`Unsupported import format: ${format}`);
    }
  }

  static fromXML(xmlString, options = {}) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');
    const parseError = doc.querySelector('parsererror');
    if (parseError) throw new Error('Invalid XML format');

    const nodes = [];
    const relationships = [];
    let root = null;

    doc.querySelectorAll('node').forEach(nodeEl => {
      const id = nodeEl.getAttribute('id');
      const isRoot = nodeEl.getAttribute('isRoot') === 'true';
      const text = nodeEl.querySelector('text')?.textContent || '';
      const color = nodeEl.querySelector('color')?.textContent || '';
      const positionEl = nodeEl.querySelector('position');
      const position = positionEl ? {
        x: parseFloat(positionEl.getAttribute('x')) || 0,
        y: parseFloat(positionEl.getAttribute('y')) || 0
      } : { x: 0, y: 0 };
      nodes.push({ id, text, position, color, isRoot });
      if (isRoot) root = id;
    });

    doc.querySelectorAll('relationship').forEach(relEl => {
      relationships.push({
        parentId: relEl.getAttribute('parentId'),
        childId: relEl.getAttribute('childId')
      });
    });

    return new MindMapState({ nodes, relationships, root, selectedId: root });
  }

  static fromCSV(csvString, options = {}) {
    const lines = csvString.trim().split('\n');
    if (lines.length < 2) throw new Error('CSV file must have at least a header and one data row');

    const headers = this.parseCSVLine(lines[0]);
    const nodes = [];
    const relationships = [];
    let root = null;

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      const rowData = {};
      headers.forEach((header, index) => {
        rowData[header.trim()] = values[index] || '';
      });

      if (!rowData.id) continue;

      const node = {
        id: rowData.id,
        text: rowData.text || '',
        position: { x: parseFloat(rowData.x) || 0, y: parseFloat(rowData.y) || 0 },
        color: rowData.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        isRoot: rowData.isRoot === 'true'
      };

      nodes.push(node);
      if (node.isRoot) root = node.id;
      if (rowData.parentId && rowData.parentId.trim()) {
        relationships.push({ parentId: rowData.parentId.trim(), childId: rowData.id });
      }
    }

    return new MindMapState({ nodes, relationships, root, selectedId: root });
  }

  static fromMarkdown(markdownString, options = {}) {
    const lines = markdownString.split('\n');
    const nodes = [];
    const relationships = [];
    let root = null;
    let currentId = 1;
    const stack = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      let text = '';
      let depth = 0;

      if (trimmedLine.startsWith('#')) {
        const match = trimmedLine.match(/^#+\s*(.+)/);
        if (match) { text = match[1]; depth = 0; }
      } else if (trimmedLine.match(/^(\s*)[-*+]\s+(.+)/)) {
        const match = trimmedLine.match(/^(\s*)[-*+]\s+(.+)/);
        text = match[2]; depth = Math.floor(match[1].length / 2) + 1;
      } else if (trimmedLine.match(/^(\s*)\d+\.\s+(.+)/)) {
        const match = trimmedLine.match(/^(\s*)\d+\.\s+(.+)/);
        text = match[2]; depth = Math.floor(match[1].length / 2) + 1;
      }

      if (text) {
        const id = `node_${currentId++}`;
        const isRoot = depth === 0;
        const node = {
          id, text,
          position: { x: depth * 200, y: nodes.length * 60 },
          color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          isRoot
        };

        nodes.push(node);

        if (isRoot) {
          root = id;
          stack.length = 1;
          stack[0] = id;
        } else {
          stack.length = depth + 1;
          stack[depth] = id;
          if (stack[depth - 1]) {
            relationships.push({ parentId: stack[depth - 1], childId: id });
          }
        }
      }
    });

    return new MindMapState({ nodes, relationships, root, selectedId: root });
  }

  static parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"'; i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current); current = '';
      } else {
        current += char;
      }
    }
    values.push(current);
    return values;
  }

  static escapeXml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
  }

  static escapeCsv(str) {
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }
}

export class DataValidator {
  static validateImportedData(mindMapState) {
    const validation = mindMapState.validate();
    const warnings = [];

    if (mindMapState.nodes.length === 0) warnings.push('Mind map contains no nodes');
    if (mindMapState.relationships.length === 0 && mindMapState.nodes.length > 1) warnings.push('Mind map has multiple nodes but no relationships');

    const emptyTextNodes = mindMapState.nodes.filter(n => !n.text.trim());
    if (emptyTextNodes.length > 0) warnings.push(`${emptyTextNodes.length} nodes have empty text`);

    const positions = mindMapState.nodes.map(n => n.position);
    const duplicatePositions = positions.filter((pos, index) =>
      positions.findIndex(p => p.x === pos.x && p.y === pos.y) !== index
    );
    if (duplicatePositions.length > 0) warnings.push('Some nodes have identical positions');

    return { ...validation, warnings };
  }

  static cleanImportedData(mindMapState) {
    const cleaned = mindMapState.clone();

    cleaned.relationships = cleaned.relationships.filter(rel => {
      const hasParent = cleaned.nodes.some(n => n.id === rel.parentId);
      const hasChild = cleaned.nodes.some(n => n.id === rel.childId);
      return hasParent && hasChild;
    });

    if (!cleaned.root || !cleaned.nodes.find(n => n.id === cleaned.root)) {
      const rootNode = cleaned.nodes.find(n => n.isRoot);
      if (rootNode) {
        cleaned.root = rootNode.id;
      } else if (cleaned.nodes.length > 0) {
        cleaned.nodes[0].isRoot = true;
        cleaned.root = cleaned.nodes[0].id;
      }
    }

    cleaned.nodes.forEach(node => { node.isRoot = node.id === cleaned.root; });

    if (cleaned.selectedId && !cleaned.nodes.find(n => n.id === cleaned.selectedId)) {
      cleaned.selectedId = cleaned.root;
    }

    return cleaned;
  }

  static remapImportedNodeIds(mindMapState, idFactory = generateImportedNodeId) {
    const remapped = mindMapState.clone();
    const idMap = new Map(
      remapped.nodes.map((node) => [node.id, idFactory()])
    );

    remapped.nodes = remapped.nodes.map((node) => ({
      ...node,
      id: idMap.get(node.id),
    }));
    remapped.relationships = remapped.relationships.map((relationship) => ({
      ...relationship,
      parentId: idMap.get(relationship.parentId),
      childId: idMap.get(relationship.childId),
    }));
    remapped.root = idMap.get(remapped.root) || null;
    remapped.selectedId = idMap.get(remapped.selectedId) || remapped.root;
    remapped.nodes.forEach((node) => {
      node.isRoot = node.id === remapped.root;
    });

    return remapped;
  }
}

export class BackupManager {
  static createBackup(mindMapState) {
    const backup = {
      timestamp: new Date().toISOString(),
      version: FORMATS.CURRENT,
      data: mindMapState.toJSON(),
      checksum: this.calculateChecksum(mindMapState)
    };
    return JSON.stringify(backup, null, 2);
  }

  static restoreFromBackup(backupString) {
    const backup = JSON.parse(backupString);
    if (backup.checksum) {
      const state = new MindMapState(backup.data);
      const currentChecksum = this.calculateChecksum(state);
      if (currentChecksum !== backup.checksum) {
        console.warn('Backup checksum mismatch - data may be corrupted');
      }
    }
    return {
      state: new MindMapState(backup.data),
      metadata: { timestamp: backup.timestamp, version: backup.version, checksum: backup.checksum }
    };
  }

  static calculateChecksum(mindMapState) {
    const data = JSON.stringify(mindMapState.toJSON());
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(16);
  }

  static createIncrementalBackup(currentState, previousState) {
    const changes = {
      timestamp: new Date().toISOString(),
      version: FORMATS.CURRENT,
      type: 'incremental'
    };

    changes.addedNodes = currentState.nodes.filter(node =>
      !previousState.nodes.find(prev => prev.id === node.id)
    );
    changes.removedNodes = previousState.nodes
      .filter(node => !currentState.nodes.find(curr => curr.id === node.id))
      .map(node => node.id);
    changes.modifiedNodes = currentState.nodes.filter(node => {
      const prevNode = previousState.nodes.find(prev => prev.id === node.id);
      return prevNode && JSON.stringify(node) !== JSON.stringify(prevNode);
    });

    const prevRelIds = new Set(previousState.relationships.map(r => `${r.parentId}-${r.childId}`));
    const currRelIds = new Set(currentState.relationships.map(r => `${r.parentId}-${r.childId}`));

    changes.addedRelationships = currentState.relationships.filter(rel =>
      !prevRelIds.has(`${rel.parentId}-${rel.childId}`)
    );
    changes.removedRelationships = previousState.relationships.filter(rel =>
      !currRelIds.has(`${rel.parentId}-${rel.childId}`)
    );

    if (currentState.root !== previousState.root) {
      changes.rootChanged = { from: previousState.root, to: currentState.root };
    }
    if (currentState.selectedId !== previousState.selectedId) {
      changes.selectionChanged = { from: previousState.selectedId, to: currentState.selectedId };
    }

    return JSON.stringify(changes, null, 2);
  }

  static getAvailableBackups(keyPrefix = 'mindMapBackup_') {
    const backups = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(keyPrefix)) {
        try {
          const data = localStorage.getItem(key);
          const backup = JSON.parse(data);
          backups.push({
            key, timestamp: backup.timestamp, size: data.length, version: backup.version || 'unknown'
          });
        } catch (error) {
          console.warn(`Invalid backup found: ${key}`, error);
        }
      }
    }
    return backups.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  static cleanupOldBackups(maxBackups = 10, keyPrefix = 'mindMapBackup_') {
    const backups = this.getAvailableBackups(keyPrefix);
    if (backups.length <= maxBackups) return 0;

    const toRemove = backups.slice(maxBackups);
    let removed = 0;
    toRemove.forEach(backup => {
      try {
        localStorage.removeItem(backup.key);
        removed++;
      } catch (error) {
        console.warn(`Failed to remove backup: ${backup.key}`, error);
      }
    });
    return removed;
  }
}

export class FileExporter {
  static download(content, filename, mimeType = 'text/plain') {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  static exportToFile(mindMapState, format, options = {}) {
    const { filename = 'mindmap' } = options;
    try {
      const content = MindMapSerializer.export(mindMapState, format, options);
      const extension = this.getFileExtension(format);
      const mimeType = this.getMimeType(format);
      this.download(content, `${filename}${extension}`, mimeType);
      return true;
    } catch (error) {
      console.error('Export failed:', error);
      return false;
    }
  }

  static getFileExtension(format) {
    const extensions = {
      [EXPORT_FORMATS.JSON]: '.json',
      [EXPORT_FORMATS.XML]: '.xml',
      [EXPORT_FORMATS.CSV]: '.csv',
      [EXPORT_FORMATS.MARKDOWN]: '.md'
    };
    return extensions[format] || '.txt';
  }

  static getMimeType(format) {
    const mimeTypes = {
      [EXPORT_FORMATS.JSON]: 'application/json',
      [EXPORT_FORMATS.XML]: 'application/xml',
      [EXPORT_FORMATS.CSV]: 'text/csv',
      [EXPORT_FORMATS.MARKDOWN]: 'text/markdown'
    };
    return mimeTypes[format] || 'text/plain';
  }
}

export class FileImporter {
  static readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (event) => reject(new Error(`Failed to read file: ${event.target.error}`));
      reader.readAsText(file);
    });
  }

  static async importFromFile(file, options = {}) {
    try {
      const content = await this.readFileAsText(file);
      const format = this.detectFormat(file.name, content);
      const mindMapState = MindMapSerializer.import(content, format, options);
      const validation = DataValidator.validateImportedData(mindMapState);
      if (!validation.isValid) console.warn('Import validation failed:', validation.errors);
      if (validation.warnings.length > 0) console.warn('Import warnings:', validation.warnings);
      const cleaned = DataValidator.cleanImportedData(mindMapState);
      return DataValidator.remapImportedNodeIds(cleaned);
    } catch (error) {
      throw new Error(`Import failed: ${error.message}`);
    }
  }

  static detectFormat(filename, content) {
    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'json': return EXPORT_FORMATS.JSON;
      case 'xml': return EXPORT_FORMATS.XML;
      case 'csv': return EXPORT_FORMATS.CSV;
      case 'md': case 'markdown': return EXPORT_FORMATS.MARKDOWN;
      default: break;
    }

    const trimmed = content.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return EXPORT_FORMATS.JSON;
    if (trimmed.startsWith('<?xml') || trimmed.startsWith('<mindmap')) return EXPORT_FORMATS.XML;
    if (trimmed.includes(',') && trimmed.split('\n').length > 1) return EXPORT_FORMATS.CSV;
    if (trimmed.includes('#') || trimmed.includes('- ') || trimmed.includes('* ')) return EXPORT_FORMATS.MARKDOWN;
    return EXPORT_FORMATS.JSON;
  }

  static validateFile(file) {
    const errors = [];
    const warnings = [];
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) errors.push(`File size (${Math.round(file.size / 1024 / 1024)}MB) exceeds maximum of 10MB`);
    const allowedExtensions = ['json', 'xml', 'csv', 'md', 'markdown', 'txt'];
    const extension = file.name.toLowerCase().split('.').pop();
    if (!allowedExtensions.includes(extension)) warnings.push(`Unsupported file extension: .${extension}`);
    if (file.size === 0) errors.push('File is empty');
    return { isValid: errors.length === 0, errors, warnings };
  }
}
